import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Upload, X, CheckCircle2, AlertCircle, ScanLine, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { parse as parseMrzRaw } from "mrz";
import { useI18n } from "@/contexts/I18nContext";

let _worker = null;
let _workerInitError = null;

/** Apply common MRZ OCR substitutions to maximize parse success. */
function applyMrzCorrections(line) {
  if (!line) return line;
  return line.replace(/[A-Z0-9<]{6,}/g, (chunk) => {
    const digitCount = (chunk.match(/[0-9]/g) || []).length;
    if (digitCount >= 3) {
      return chunk
        .replace(/O/g, "0").replace(/Q/g, "0").replace(/D/g, "0")
        .replace(/I/g, "1").replace(/L/g, "1")
        .replace(/Z/g, "2")
        .replace(/S/g, "5")
        .replace(/G/g, "6")
        .replace(/B/g, "8");
    }
    return chunk;
  });
}

async function getWorker() {
  if (_worker) return _worker;
  if (_workerInitError) throw _workerInitError;
  try {
    const Tesseract = await import("tesseract.js");
    // Use bundled local assets (under /tesseract/) to avoid CDN failures
    // (slow networks / blocked CDNs cause "تعذّر تشغيل محرك القراءة").
    const base = `${window.location.origin}/tesseract`;
    _worker = await Tesseract.createWorker("eng", 1, {
      workerPath: `${base}/worker.min.js`,
      langPath: `${base}/lang`,
      corePath: `${base}/core`,
      workerBlobURL: false,
      gzip: true,
    });
    await _worker.setParameters({
      tessedit_char_whitelist:
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<",
      preserve_interword_spaces: "0",
      tessedit_pageseg_mode: "6",
    });
    return _worker;
  } catch (e) {
    _workerInitError = e;
    throw e;
  }
}

/** Extract MRZ candidate lines from raw OCR text — TD1 / TD2 / TD3 tolerant. */
function extractMrzLines(rawText) {
  if (!rawText) return null;
  const cleaned = rawText
    .split(/\r?\n/)
    .map((l) => l.toUpperCase().replace(/[^A-Z0-9<]/g, ""))
    .filter((l) => l.length >= 24);
  if (cleaned.length === 0) return null;
  for (let i = 0; i < cleaned.length - 1; i++) {
    const a = cleaned[i], b = cleaned[i + 1];
    if (a.length >= 40 && b.length >= 40) {
      return [a.padEnd(44, "<").slice(0, 44), b.padEnd(44, "<").slice(0, 44)];
    }
  }
  for (let i = 0; i < cleaned.length - 1; i++) {
    const a = cleaned[i], b = cleaned[i + 1];
    if (a.length >= 32 && a.length <= 40 && b.length >= 32 && b.length <= 40) {
      return [a.padEnd(36, "<").slice(0, 36), b.padEnd(36, "<").slice(0, 36)];
    }
  }
  for (let i = 0; i < cleaned.length - 2; i++) {
    const [a, b, c] = [cleaned[i], cleaned[i + 1], cleaned[i + 2]];
    if (a.length >= 26 && a.length <= 34 &&
        b.length >= 26 && b.length <= 34 &&
        c.length >= 26 && c.length <= 34) {
      return [
        a.padEnd(30, "<").slice(0, 30),
        b.padEnd(30, "<").slice(0, 30),
        c.padEnd(30, "<").slice(0, 30),
      ];
    }
  }
  return null;
}

function mrzDate(yymmdd, isBirth = true) {
  if (!yymmdd || !/^\d{6}$/.test(yymmdd)) return "";
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  const year = isBirth ? (yy <= 30 ? 2000 + yy : 1900 + yy) : 2000 + yy;
  return `${year}-${mm}-${dd}`;
}

function normalizeMrzResult(parsed) {
  if (!parsed || !parsed.fields) return null;
  const f = parsed.fields;
  const fullName = [f.firstName, f.lastName].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return {
    ok: parsed.valid !== false,
    lenient: parsed.valid === false,
    format: parsed.format,
    document_type: f.documentCode || "",
    document_number: (f.documentNumber || "").replace(/</g, ""),
    nationality: f.nationality || "",
    issuing_country: f.issuingState || "",
    full_name: fullName,
    first_name: f.firstName || "",
    last_name: f.lastName || "",
    sex: f.sex || "",
    date_of_birth: mrzDate(f.birthDate, true),
    expiry_date: mrzDate(f.expirationDate, false),
    raw: parsed,
  };
}

function tryParseFromText(text) {
  const lines = extractMrzLines(text);
  if (!lines) return { lines: null, parsed: null };
  try {
    const raw = parseMrzRaw(lines);
    const r = normalizeMrzResult(raw);
    if (r && r.ok && r.document_number) return { lines, parsed: r };
  } catch { /* try lenient */ }
  const corrected = lines.map(applyMrzCorrections);
  try {
    const raw2 = parseMrzRaw(corrected);
    const r2 = normalizeMrzResult(raw2);
    if (r2 && r2.document_number && (r2.date_of_birth || r2.expiry_date)) {
      return { lines: corrected, parsed: r2 };
    }
  } catch { /* fallthrough */ }
  try {
    const raw3 = parseMrzRaw(lines);
    const r3 = normalizeMrzResult(raw3);
    if (r3 && r3.document_number) return { lines, parsed: r3 };
  } catch { /* swallow */ }
  return { lines, parsed: null };
}

/* ───────────────────────── Document SVG illustrations ───────────────────────── */
function PassportSVG({ theme }) {
  const gold = theme.gold || "#d4af37";
  return (
    <svg viewBox="0 0 320 200" width="100%" style={{ maxWidth: 280, display: "block", margin: "0 auto" }} aria-hidden>
      {/* Open passport spread */}
      <rect x="10" y="20" width="300" height="160" rx="10" fill={theme.surface} stroke={gold} strokeWidth="2" />
      <line x1="160" y1="20" x2="160" y2="180" stroke={gold} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.55" />
      {/* Left page: photo + lines */}
      <rect x="22" y="32" width="50" height="60" rx="3" fill={theme.borderSoft} stroke={gold} strokeWidth="0.8" />
      <line x1="80" y1="40" x2="148" y2="40" stroke={theme.textMuted} strokeWidth="2" />
      <line x1="80" y1="50" x2="148" y2="50" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.7" />
      <line x1="80" y1="60" x2="138" y2="60" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.7" />
      <line x1="80" y1="72" x2="128" y2="72" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.6" />
      <line x1="80" y1="82" x2="120" y2="82" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.55" />
      {/* MRZ band (target) — pulsing highlight */}
      <rect x="22" y="118" width="126" height="48" rx="3" fill={gold} fillOpacity="0.14" stroke={gold} strokeWidth="1.5" strokeDasharray="4 2" />
      <line x1="30" y1="130" x2="138" y2="130" stroke={gold} strokeWidth="2" />
      <line x1="30" y1="140" x2="138" y2="140" stroke={gold} strokeWidth="2" />
      <line x1="30" y1="150" x2="138" y2="150" stroke={gold} strokeWidth="2" opacity="0.8" />
      {/* Right page: faded */}
      <rect x="172" y="32" width="124" height="120" rx="3" fill="none" stroke={theme.borderSoft} strokeWidth="0.6" opacity="0.55" />
      {/* Arrow pointing at MRZ */}
      <g style={{ transformOrigin: "85px 142px" }}>
        <path d="M 85 105 L 85 118" stroke={gold} strokeWidth="2.4" fill="none" markerEnd="url(#arr1)" />
      </g>
      <defs>
        <marker id="arr1" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6 z" fill={gold} />
        </marker>
      </defs>
    </svg>
  );
}

function NationalIdSVG({ theme }) {
  const gold = theme.gold || "#d4af37";
  return (
    <svg viewBox="0 0 320 200" width="100%" style={{ maxWidth: 280, display: "block", margin: "0 auto" }} aria-hidden>
      {/* ID card (back side) */}
      <rect x="40" y="30" width="240" height="140" rx="14" fill={theme.surface} stroke={gold} strokeWidth="2" />
      {/* "Back" label */}
      <text x="160" y="50" textAnchor="middle" fill={gold} fontSize="11" fontWeight="800" fontFamily="monospace">BACK SIDE — الوجه الخلفي</text>
      {/* QR code mock */}
      <rect x="55" y="62" width="56" height="56" rx="3" fill={theme.borderSoft} stroke={gold} strokeWidth="0.6" />
      <g fill={gold} opacity="0.55">
        <rect x="60" y="67" width="10" height="10" />
        <rect x="96" y="67" width="10" height="10" />
        <rect x="60" y="103" width="10" height="10" />
        <rect x="73" y="80" width="6" height="6" />
        <rect x="85" y="88" width="5" height="5" />
        <rect x="78" y="95" width="6" height="6" />
        <rect x="92" y="95" width="6" height="6" />
        <rect x="73" y="103" width="5" height="5" />
      </g>
      {/* MRZ band */}
      <rect x="55" y="128" width="210" height="32" rx="2" fill={gold} fillOpacity="0.14" stroke={gold} strokeWidth="1.5" strokeDasharray="4 2" />
      <line x1="62" y1="138" x2="258" y2="138" stroke={gold} strokeWidth="2" />
      <line x1="62" y1="148" x2="258" y2="148" stroke={gold} strokeWidth="2" />
      <line x1="62" y1="156" x2="200" y2="156" stroke={gold} strokeWidth="2" opacity="0.7" />
      {/* Info block */}
      <line x1="125" y1="68" x2="260" y2="68" stroke={theme.textMuted} strokeWidth="2" />
      <line x1="125" y1="78" x2="240" y2="78" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.7" />
      <line x1="125" y1="90" x2="220" y2="90" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.7" />
      <line x1="125" y1="100" x2="240" y2="100" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.6" />
      <line x1="125" y1="112" x2="200" y2="112" stroke={theme.textMuted} strokeWidth="1.5" opacity="0.55" />
      {/* Down arrow into MRZ band */}
      <defs>
        <marker id="arr2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6 z" fill={gold} />
        </marker>
      </defs>
      <path d="M 165 116 L 165 128" stroke={gold} strokeWidth="2.4" fill="none" markerEnd="url(#arr2)" />
    </svg>
  );
}

function DocIllustration({ docType, theme }) {
  if (docType === "national_id" || docType === "residence" || docType === "iqama") {
    return <NationalIdSVG theme={theme} />;
  }
  return <PassportSVG theme={theme} />;
}

/* ───────────────────────── Component ───────────────────────── */
export default function SmartScanModal({ open, onClose, onResult, docType = "passport" }) {
  const { lang, theme, dir } = useI18n();
  const T = lang === "ar"
    ? {
        title: "المسح الذكي للمستند",
        passport_guide_title: "صوّر صفحة بيانات الجواز",
        passport_guide_body: "افتح الجواز على الصفحة التي تحوي صورتك الشخصية ومعلوماتك. حاذِ المستطيل واحرص على وضوح السطرين السفليين (MRZ).",
        id_guide_title: "صوّر الجهة الخلفية للهوية",
        id_guide_body: "اقلب البطاقة على الوجه الخلفي حيث يوجد رمز QR والمعلومات. تأكد من إضاءة جيدة وعدم الانعكاسات.",
        capture_btn: "التقاط صورة",
        gallery_btn: "اختيار من المعرض",
        type_manual: "إدخال البيانات يدوياً",
        cancel: "إلغاء",
        processing: "جاري قراءة المستند…",
        processing_hint: "نستخدم نظام التحقق المعتمد — لحظات",
        success: "تمت قراءة المستند بنجاح",
        success_review: "راجع البيانات — يمكنك تعديل أي خانة قبل التأكيد",
        warn_lenient: "تم الاستخراج بثقة جيدة — تحقق من الحقول قبل المتابعة",
        failed: "لم نتمكن من قراءة المستند",
        failed_hint: "حاول بصورة أوضح وأقرب — أو أدخل البيانات يدوياً",
        retry: "محاولة بصورة أخرى",
        confirm_data: "تأكيد البيانات",
        f_name: "الاسم الكامل",
        f_doc_no: "رقم المستند",
        f_nationality: "الجنسية",
        f_dob: "تاريخ الميلاد",
        f_exp: "تاريخ الانتهاء",
        f_sex: "الجنس",
        beta: "ميزة مساندة — اختيارية",
        tip_lighting: "إضاءة كافية بدون انعكاسات",
        tip_flat: "ضع المستند على سطح مستوٍ",
        tip_full: "اظهر المستند بالكامل داخل الصورة",
      }
    : {
        title: "Smart Document Scan",
        passport_guide_title: "Photograph the passport info page",
        passport_guide_body: "Open the passport at the page with your photo and information. Frame the document fully and make sure the bottom two MRZ lines are sharp.",
        id_guide_title: "Photograph the back side of the ID",
        id_guide_body: "Flip the card to the back where the QR code and information are located. Ensure good lighting and no glare.",
        capture_btn: "Take photo",
        gallery_btn: "Pick from gallery",
        type_manual: "Enter data manually",
        cancel: "Cancel",
        processing: "Reading the document…",
        processing_hint: "Using the certified verification engine — a few seconds",
        success: "Document read successfully",
        success_review: "Review the data — edit any field before confirming",
        warn_lenient: "Extracted with good confidence — please verify before continuing",
        failed: "Couldn't read the document",
        failed_hint: "Try a clearer, closer photo — or enter the data manually",
        retry: "Try another photo",
        confirm_data: "Confirm",
        f_name: "Full name",
        f_doc_no: "Document number",
        f_nationality: "Nationality",
        f_dob: "Date of birth",
        f_exp: "Expiry date",
        f_sex: "Sex",
        beta: "Optional helper feature",
        tip_lighting: "Sufficient lighting, no glare",
        tip_flat: "Place document on a flat surface",
        tip_full: "Show the full document in the photo",
      };

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const mountedRef = useRef(true);
  const busyRef = useRef(false);

  // Phases: idle | processing | review | failed | manual
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [frozenDataUrl, setFrozenDataUrl] = useState(null);
  const [error, setError] = useState("");
  const [lastOcrText, setLastOcrText] = useState("");
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  // Manual entry form state (used when phase==="manual")
  const [manualForm, setManualForm] = useState({
    full_name: "", document_number: "", nationality: "",
    date_of_birth: "", expiry_date: "", sex: "",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    // Reset state every time the modal opens
    setPhase("idle");
    setResult(null);
    setCapturedBlob(null);
    setFrozenDataUrl(null);
    setError("");
    setLastOcrText("");
    setShowDiagnostic(false);
    setManualForm({
      full_name: "", document_number: "", nationality: "",
      date_of_birth: "", expiry_date: "", sex: "",
    });
    busyRef.current = false;
  }, [open]);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    setError("");
    setPhase("processing");
    busyRef.current = true;
    setCapturedBlob(file);

    // Generate the in-memory preview so the failed-state (if any) can still
    // show the captured image.
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setFrozenDataUrl(dataUrl);
    } catch (_e) { /* ignore preview errors */ }

    // Skip local Tesseract entirely — go DIRECTLY to the certified
    // self-reading engine (Gemini Vision). Per product decision: the local
    // engine produced too many false-failures and confused clients with
    // repeated "tap the manual fallback" prompts. The remote engine is
    // robust enough to be the primary path.
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "doc.jpg");
      fd.append("doc_type", docType || "passport");
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/portal/ocr/passport`, { method: "POST", body: fd });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      const p = data.parsed || {};
      if (p.valid_document === false || !p.full_name || !p.document_number || !p.date_of_birth || !p.expiry_date) {
        setError(lang === "ar"
          ? "تعذّرت قراءة الوثيقة. الرجاء التقاط صورة أوضح."
          : "Couldn't read the document. Please take a clearer photo.");
        setPhase("failed");
        return;
      }
      // Pre-fill the verify-form with whatever the engine read so the user
      // can review and confirm before saving.
      setManualForm({
        full_name: p.full_name || "",
        document_number: p.document_number || "",
        nationality: p.nationality || "",
        date_of_birth: p.date_of_birth || "",
        expiry_date: p.expiry_date || "",
        sex: p.sex || "",
      });
      setPhase("manual");
    } catch (e) {
      if (!mountedRef.current) return;
      setError(lang === "ar"
        ? `تعذّرت القراءة الذاتية: ${e?.message || ""}`
        : `Auto recognition failed: ${e?.message || ""}`);
      setPhase("failed");
    } finally {
      busyRef.current = false;
    }
  }, [lang, docType]);

  const onTypeManually = () => {
    // Open the inline manual data entry form — let the user TYPE the document
    // info themselves. After save, we emit {mode:'manual_typed', parsed:{...}}.
    setError("");
    setPhase("manual");
  };

  const onSaveManual = () => {
    const trimmed = {
      ok: true,
      lenient: false,
      full_name: manualForm.full_name.trim(),
      document_number: manualForm.document_number.trim().toUpperCase(),
      nationality: manualForm.nationality.trim().toUpperCase(),
      date_of_birth: manualForm.date_of_birth || "",
      expiry_date: manualForm.expiry_date || "",
      sex: manualForm.sex.trim().toUpperCase(),
    };
    if (!trimmed.full_name || !trimmed.document_number || !trimmed.date_of_birth || !trimmed.expiry_date) {
      setError(lang === "ar" ? "أكمل الاسم ورقم الوثيقة وتاريخ الميلاد وتاريخ الانتهاء" : "Complete name, document number, birth date and expiry date");
      return;
    }
    // Preserve the photo that was captured (if any) so the office still receives
    // the document evidence even when OCR couldn't parse it.
    onResult({ mode: "manual_typed", file: capturedBlob || null, parsed: trimmed });
    onClose();
  };

  /* When the user closes the modal mid-way, do not silently drop a
     captured photo. If they captured / picked a file at any point we
     attach it to the document — even if OCR failed or was skipped.
     This implements management's directive: "ارفع + اقرأ" دفعة واحدة. */
  const handleClose = () => {
    if (capturedBlob) {
      onResult({ mode: "attached_only", file: capturedBlob, parsed: null });
    }
    onClose();
  };

  const onConfirm = () => {
    onResult({ mode: "scanned", file: capturedBlob, parsed: result });
    onClose();
  };

  const onRetry = () => {
    setPhase("idle"); setError(""); setResult(null);
    setCapturedBlob(null); setFrozenDataUrl(null);
    setLastOcrText(""); setShowDiagnostic(false);
  };

  /**
   * AI Vision fallback (Gemini). The user hits this when Tesseract failed —
   * we re-submit the SAME captured image to our backend, which calls Gemini
   * Vision. Gemini handles rotation, low light, holograms etc. ~$0.001/call.
   */
  const [aiBusy, setAiBusy] = useState(false);
  const tryAiOcr = useCallback(async () => {
    if (!capturedBlob || aiBusy) return;
    setAiBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", capturedBlob, "doc.jpg");
      // Tell the backend which kind of document we're scanning so it can pick
      // the right prompt (passport / iqama / national_id).
      fd.append("doc_type", docType || "passport");
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/portal/ocr/passport`, { method: "POST", body: fd });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const p = data.parsed || {};
      if (!p.full_name && !p.document_number) {
        setError(lang === "ar"
          ? "تعذّرت قراءة الوثيقة. الرجاء التقاط صورة أوضح أو الكتابة يدوياً."
          : "Couldn't read the document. Take a clearer photo or type manually.");
        return;
      }
      // Merge AI result into manual form so the user can verify before saving
      setManualForm({
        full_name: p.full_name || "",
        document_number: p.document_number || "",
        nationality: p.nationality || "",
        date_of_birth: p.date_of_birth || "",
        expiry_date: p.expiry_date || "",
        sex: p.sex || "",
      });
      setPhase("manual");
    } catch (e) {
      setError(lang === "ar"
        ? `تعذّرت القراءة الذاتية: ${e.message || ""}`
        : `Auto recognition failed: ${e.message || ""}`);
    } finally {
      setAiBusy(false);
    }
  }, [capturedBlob, aiBusy, lang, docType]);

  if (!open) return null;
  const css = styles(theme, dir);
  const isIdLike = docType === "national_id" || docType === "residence" || docType === "iqama";
  const guideTitle = isIdLike ? T.id_guide_title : T.passport_guide_title;
  const guideBody = isIdLike ? T.id_guide_body : T.passport_guide_body;

  return (
    <div style={css.bg} onClick={handleClose}>
      <div style={css.card} onClick={(e) => e.stopPropagation()} data-testid="smart-scan-modal">
        <div style={css.head}>
          <span style={css.title}>
            <ScanLine size={18} strokeWidth={2.4} /> {T.title}
          </span>
          <button onClick={handleClose} style={css.close} aria-label="close" data-testid="scan-close">
            <X size={18} strokeWidth={2.2} />
          </button>
        </div>
        <div style={css.betaTag}>{T.beta}</div>

        {phase === "idle" && (
          <>
            <div style={css.guideBox} data-testid="scan-guide-card">
              <DocIllustration docType={docType} theme={theme} />
              <div style={css.guideTitle}>{guideTitle}</div>
              <div style={css.guideBody}>{guideBody}</div>
              <div style={css.tipRow}>
                <span style={css.tipChip}>💡 {T.tip_lighting}</span>
                <span style={css.tipChip}>📄 {T.tip_flat}</span>
                <span style={css.tipChip}>🖼 {T.tip_full}</span>
              </div>
            </div>
            <button onClick={() => cameraInputRef.current?.click()} style={css.btnGoldBig} data-testid="scan-capture-photo">
              <Camera size={18} strokeWidth={2.4} /> {T.capture_btn}
            </button>
            <button onClick={() => galleryInputRef.current?.click()} style={css.btnGoldOutlineFull} data-testid="scan-pick-gallery">
              <ImageIcon size={15} strokeWidth={2.2} /> {T.gallery_btn}
            </button>
            <button onClick={onTypeManually} style={css.btnGhostFull} data-testid="scan-manual">
              {T.type_manual}
            </button>
            {/* Camera input — opens the device camera (no getUserMedia permission needed) */}
            <input
              ref={cameraInputRef} type="file"
              accept="image/png,image/jpeg,image/webp"
              capture="environment"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) processFile(f);
                if (e.target) e.target.value = "";
              }}
              data-testid="scan-camera-input"
            />
            {/* Gallery input — no capture attribute → opens file picker */}
            <input
              ref={galleryInputRef} type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) processFile(f);
                if (e.target) e.target.value = "";
              }}
              data-testid="scan-gallery-input"
            />
          </>
        )}

        {phase === "processing" && (
          <div style={css.processingBox} data-testid="scan-processing">
            <RefreshCw size={32} strokeWidth={2.4} className="vta-spin" style={{ color: theme.gold }} />
            <div style={{ fontWeight: 900, fontSize: 15, marginTop: 12 }}>{T.processing}</div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{T.processing_hint}</div>
            {frozenDataUrl && (
              <img src={frozenDataUrl} alt="processing" style={{ marginTop: 14, width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8, opacity: 0.6 }} />
            )}
          </div>
        )}

        {phase === "review" && result && (
          <ReviewForm
            result={result} setResult={setResult} T={T} theme={theme}
            frozenDataUrl={frozenDataUrl}
            onConfirm={onConfirm}
            onCancel={handleClose}
          />
        )}

        {phase === "failed" && (
          <FailedState
            T={T} theme={theme}
            error={error}
            frozenDataUrl={frozenDataUrl}
            onRetry={onRetry}
            onTypeManually={onTypeManually}
            onTryAi={tryAiOcr}
            aiBusy={aiBusy}
            canTryAi={!!capturedBlob}
            lang={lang}
            lastOcrText={lastOcrText}
            showDiagnostic={showDiagnostic}
            setShowDiagnostic={setShowDiagnostic}
          />
        )}

        {phase === "manual" && (
          <ManualEntryForm
            T={T} theme={theme} lang={lang}
            form={manualForm} setForm={setManualForm}
            error={error}
            onCancel={() => { setError(""); setPhase("idle"); }}
            onSave={onSaveManual}
          />
        )}

        <style>{`
          .vta-spin { animation: vta-spin 1s linear infinite; }
          @keyframes vta-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </div>
  );
}

function ReviewForm({ result, setResult, T, theme, frozenDataUrl, onConfirm, onCancel }) {
  const css = styles(theme);
  const update = (k, v) => setResult((r) => ({ ...r, [k]: v }));
  return (
    <>
      {frozenDataUrl && (
        <div style={css.frozenWrap} data-testid="scan-frozen-frame">
          <img src={frozenDataUrl} alt="captured" style={css.frozenImg} />
          <div style={css.frozenBadge}>
            <CheckCircle2 size={14} strokeWidth={2.4} /> {T.success}
          </div>
        </div>
      )}
      <div style={css.successBar}>
        <CheckCircle2 size={16} strokeWidth={2.4} /> {T.success_review}
      </div>
      {result.lenient && (
        <div style={css.warnBar} data-testid="scan-lenient-warn">
          <AlertTriangle size={14} strokeWidth={2.4} /> {T.warn_lenient}
        </div>
      )}
      <div style={css.formGrid}>
        <Field label={T.f_name}>
          <input style={css.input} value={result.full_name || ""} onChange={(e) => update("full_name", e.target.value)} data-testid="scan-result-name" />
        </Field>
        <Field label={T.f_doc_no}>
          <input style={{ ...css.input, fontFamily: "monospace" }} dir="ltr" value={result.document_number || ""} onChange={(e) => update("document_number", e.target.value)} data-testid="scan-result-docno" />
        </Field>
        <Field label={T.f_nationality}>
          <input style={css.input} value={result.nationality || ""} onChange={(e) => update("nationality", e.target.value.toUpperCase())} data-testid="scan-result-nat" />
        </Field>
        <Field label={T.f_dob}>
          <input style={css.input} type="date" value={result.date_of_birth || ""} onChange={(e) => update("date_of_birth", e.target.value)} data-testid="scan-result-dob" />
        </Field>
        <Field label={T.f_exp}>
          <input style={css.input} type="date" value={result.expiry_date || ""} onChange={(e) => update("expiry_date", e.target.value)} data-testid="scan-result-exp" />
        </Field>
        <Field label={T.f_sex}>
          <input style={css.input} value={result.sex || ""} onChange={(e) => update("sex", e.target.value.toUpperCase())} data-testid="scan-result-sex" />
        </Field>
      </div>
      <div style={css.btnRow}>
        <button onClick={onCancel} style={css.btnGhost} data-testid="scan-discard">{T.cancel}</button>
        <button onClick={onConfirm} style={css.btnGold} data-testid="scan-confirm">{T.confirm_data}</button>
      </div>
    </>
  );
}

function FailedState({ T, theme, error, frozenDataUrl, onRetry, onTypeManually, onTryAi, aiBusy, canTryAi, lang, lastOcrText, showDiagnostic, setShowDiagnostic }) {
  const css = styles(theme);
  return (
    <>
      <div style={css.failedBar}>
        <AlertCircle size={20} strokeWidth={2.2} />
        <div>
          <div style={{ fontWeight: 900, fontSize: 14 }}>{error || T.failed}</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{T.failed_hint}</div>
        </div>
      </div>
      {frozenDataUrl && (
        <img src={frozenDataUrl} alt="last capture" style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 10, marginBottom: 12, opacity: 0.7 }} />
      )}
      <div style={css.btnCol}>
        {canTryAi && (
          <button onClick={onTryAi} disabled={aiBusy} style={{ ...css.btnGold, opacity: aiBusy ? 0.6 : 1 }}
            data-testid="scan-try-ai">
            <ScanLine size={14} strokeWidth={2.3} />
            {aiBusy
              ? (lang === "ar" ? "...جاري التحقق المعتمد" : "...verifying")
              : (lang === "ar" ? "📄 القراءة الذاتية للوثيقة" : "📄 Auto Document Recognition")}
          </button>
        )}
        <button onClick={onRetry} style={css.btnGhost} data-testid="scan-retry">
          <Camera size={14} strokeWidth={2.3} /> {T.retry}
        </button>
        <button onClick={onTypeManually} style={css.btnGhost} data-testid="scan-fallback-type">
          {T.type_manual}
        </button>
      </div>
      {lastOcrText && (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => setShowDiagnostic((v) => !v)}
            style={{
              background: "transparent", border: "none", color: theme.textMuted,
              fontSize: 11, cursor: "pointer", textDecoration: "underline dotted",
              padding: 0, fontFamily: "inherit",
            }}
            data-testid="scan-diagnostic-toggle"
          >
            {showDiagnostic ? "إخفاء التشخيص" : "🛠 رؤية ما قرأه النظام"}
          </button>
          {showDiagnostic && (
            <pre
              data-testid="scan-diagnostic-text"
              style={{
                marginTop: 8, padding: 10,
                background: theme.inputBg, border: `1px solid ${theme.borderSoft}`,
                borderRadius: 8, color: theme.textSoft, fontSize: 10,
                fontFamily: "monospace", direction: "ltr", textAlign: "left",
                maxHeight: 180, overflow: "auto", whiteSpace: "pre-wrap",
              }}
            >{lastOcrText}</pre>
          )}
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4, letterSpacing: 0.4 }}>{label}</div>
      {children}
    </label>
  );
}

function ManualEntryForm({ T, theme, lang, form, setForm, error, onCancel, onSave }) {
  const css = styles(theme);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const hasPrefill = !!(form.full_name || form.document_number || form.date_of_birth);
  const bannerText = hasPrefill
    ? (lang === "ar"
        ? "تمت قراءة الوثيقة — راجع البيانات وعدّل أي حقل قبل التأكيد."
        : "Document read successfully — review and edit any field before confirming.")
    : (lang === "ar"
        ? "أدخل بيانات المستند يدوياً. حقل الاسم مطلوب — البقية اختيارية."
        : "Enter document data manually. Full name is required — other fields are optional.");
  return (
    <div data-testid="scan-manual-form">
      <div style={{
        background: hasPrefill
          ? (theme.name === "dark" ? "rgba(22,163,74,0.10)" : "rgba(22,163,74,0.08)")
          : (theme.name === "dark" ? "rgba(212,175,55,0.10)" : "rgba(184,144,42,0.10)"),
        border: `1px solid ${hasPrefill ? "#16a34a" : theme.borderAccent}`,
        color: hasPrefill ? "#16a34a" : theme.gold, padding: "10px 14px", borderRadius: 10,
        fontSize: 12, fontWeight: 700, marginBottom: 14, lineHeight: 1.7,
      }}>
        {bannerText}
      </div>
      <div style={css.formGrid}>
        <Field label={T.f_name}>
          <input
            style={css.input} value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            placeholder={lang === "ar" ? "كما يظهر في الجواز" : "As shown on the passport"}
            data-testid="scan-manual-name" autoFocus
          />
        </Field>
        <Field label={T.f_doc_no}>
          <input
            style={{ ...css.input, fontFamily: "monospace" }} dir="ltr"
            value={form.document_number}
            onChange={(e) => update("document_number", e.target.value)}
            placeholder="A12345678"
            data-testid="scan-manual-docno"
          />
        </Field>
        <Field label={T.f_nationality}>
          <input
            style={css.input} value={form.nationality}
            onChange={(e) => update("nationality", e.target.value.toUpperCase())}
            placeholder="SAU / EGY / SYR …"
            data-testid="scan-manual-nat"
          />
        </Field>
        <Field label={T.f_dob}>
          <input
            style={css.input} type="date" value={form.date_of_birth}
            onChange={(e) => update("date_of_birth", e.target.value)}
            data-testid="scan-manual-dob"
          />
        </Field>
        <Field label={T.f_exp}>
          <input
            style={css.input} type="date" value={form.expiry_date}
            onChange={(e) => update("expiry_date", e.target.value)}
            data-testid="scan-manual-exp"
          />
        </Field>
        <Field label={T.f_sex}>
          <select
            style={css.input} value={form.sex}
            onChange={(e) => update("sex", e.target.value)}
            data-testid="scan-manual-sex"
          >
            <option value="">—</option>
            <option value="M">{lang === "ar" ? "ذكر (M)" : "Male (M)"}</option>
            <option value="F">{lang === "ar" ? "أنثى (F)" : "Female (F)"}</option>
          </select>
        </Field>
      </div>
      {error && (
        <div style={{
          background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
          color: theme.errorText, padding: "8px 12px", borderRadius: 8,
          fontSize: 12, fontWeight: 700, marginBottom: 12,
        }} data-testid="scan-manual-error">{error}</div>
      )}
      <div style={css.btnRow}>
        <button onClick={onCancel} style={css.btnGhost} data-testid="scan-manual-cancel">
          {lang === "ar" ? "رجوع" : "Back"}
        </button>
        <button onClick={onSave} style={css.btnGold} data-testid="scan-manual-save">
          <CheckCircle2 size={15} strokeWidth={2.4} /> {T.confirm_data}
        </button>
      </div>
    </div>
  );
}

const styles = (theme) => ({
  bg: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
  },
  card: {
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 16, padding: 16, width: "100%", maxWidth: 480,
    maxHeight: "96vh", overflowY: "auto",
    fontFamily: "'Tajawal','Segoe UI',sans-serif", color: theme.text,
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 },
  title: { display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 900, color: theme.gold, fontSize: 15 },
  close: { background: "transparent", border: "none", color: theme.text, cursor: "pointer", padding: 4 },
  betaTag: {
    fontSize: 10, fontWeight: 700, color: theme.textMuted,
    border: `1px dashed ${theme.borderSoft}`, padding: "3px 8px",
    borderRadius: 999, display: "inline-block", marginBottom: 12,
  },
  guideBox: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14, padding: "16px 14px", marginBottom: 14,
    textAlign: "center",
  },
  guideTitle: {
    fontWeight: 900, fontSize: 15, color: theme.gold, marginTop: 12, marginBottom: 6,
  },
  guideBody: {
    fontSize: 12, color: theme.textSoft, lineHeight: 1.7,
    marginBottom: 12,
  },
  tipRow: {
    display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center",
  },
  tipChip: {
    fontSize: 10, padding: "4px 10px", borderRadius: 999,
    background: theme.name === "dark" ? "rgba(212,175,55,0.10)" : "rgba(184,144,42,0.10)",
    color: theme.gold, fontWeight: 700,
    border: `1px solid ${theme.borderSoft}`,
  },
  btnGoldBig: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    width: "100%", padding: "16px 18px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 12, fontWeight: 900,
    cursor: "pointer", fontSize: 15, marginBottom: 8,
    fontFamily: "inherit",
    minHeight: 52,
    boxShadow: "0 4px 16px rgba(212,175,55,0.20)",
  },
  btnGoldOutlineFull: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    width: "100%", padding: "13px 16px",
    background: "transparent", color: theme.gold,
    border: `1.5px solid ${theme.borderAccent}`, borderRadius: 12,
    fontWeight: 800, cursor: "pointer", fontSize: 13,
    marginBottom: 8, fontFamily: "inherit",
    minHeight: 46,
  },
  btnGhostFull: {
    display: "block",
    width: "100%", padding: "12px 16px",
    background: "transparent", color: theme.textMuted,
    border: `1px dashed ${theme.borderSoft}`, borderRadius: 12,
    fontWeight: 700, cursor: "pointer", fontSize: 12,
    fontFamily: "inherit",
    minHeight: 42,
  },
  processingBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "30px 16px",
  },
  frozenWrap: {
    position: "relative", width: "100%",
    border: "3px solid #16a34a", borderRadius: 12, overflow: "hidden",
    marginBottom: 12,
    boxShadow: "0 0 0 4px rgba(22,163,74,0.18)",
  },
  frozenImg: { width: "100%", display: "block", maxHeight: 240, objectFit: "cover" },
  frozenBadge: {
    position: "absolute", top: 8, insetInlineEnd: 8,
    background: "#16a34a", color: "#fff",
    padding: "5px 10px", borderRadius: 999,
    fontSize: 11, fontWeight: 900,
    display: "inline-flex", alignItems: "center", gap: 5,
  },
  warnBar: {
    display: "flex", gap: 8, alignItems: "center",
    background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.45)",
    color: "#b45309", padding: "9px 12px", borderRadius: 10,
    fontSize: 12, fontWeight: 700, marginBottom: 12,
  },
  btnRow: { display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" },
  btnCol: { display: "flex", flexDirection: "column", gap: 8 },
  btnGhost: {
    padding: "12px 18px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 10,
    fontWeight: 700, cursor: "pointer", fontSize: 13,
    minHeight: 46, flex: 1,
  },
  btnGold: {
    padding: "12px 18px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 10, fontWeight: 900,
    cursor: "pointer", fontSize: 13,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    minHeight: 46, flex: 1,
  },
  successBar: {
    display: "flex", gap: 8, alignItems: "center",
    background: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.40)",
    color: "#16a34a", padding: "10px 12px", borderRadius: 10,
    fontSize: 13, fontWeight: 700, marginBottom: 12,
  },
  failedBar: {
    display: "flex", gap: 12, alignItems: "flex-start",
    background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: 14, borderRadius: 12,
    marginBottom: 14, lineHeight: 1.6,
  },
  formGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10, marginBottom: 14,
  },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
    color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box",
    minHeight: 42,
  },
});
